import axios from 'axios';
import React, { useCallback, useContext, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import UserContext from '../../context/UserContext/UserContext';
import useAbortController from '../../hooks/useAbortController';
import AuthService from '../../services/AuthService/AuthService';
import UserService from '../../services/UserService/UserService';

function RequireFullAuth(): React.ReactElement | null {
  const { user, setUser, isLoading, setIsLoading } = useContext(UserContext);
  const location = useLocation();

  const { getAbortSignal } = useAbortController();

  const fetchUser = useCallback(async (): Promise<void> => {
    if (!AuthService.getToken()) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    const signal = getAbortSignal();
    try {
      const fetchedUser = await UserService.loggedInUser(signal);
      setUser(fetchedUser);
    } catch (error: any) {
      if (axios.isCancel(error)) return;
      setUser(null);
      if (error.response?.status === 401) {
        await AuthService.logout();
      }
    }
    setIsLoading(false);
  }, [setUser, setIsLoading, getAbortSignal]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (isLoading) {
    return null;
  }

  if (!user) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }
  if (!user.mfaComplete) {
    return <Navigate to='/2fa' state={{ from: location }} replace />;
  }

  return <Outlet />;
}

export default RequireFullAuth;
